import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Quote, ArrowLeft, Users } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import axios from "axios";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferenzenPage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/testimonials/approved`).then(r => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  usePageSeo({
    title: "Referenzen & Kundenstimmen | LAB336",
    description: "Erfahren Sie, was unsere Kunden über die Zusammenarbeit mit LAB336 sagen. Echte Erfahrungsberichte zu Prozessoptimierung, Digitalisierung und KI-Beratung.",
    canonical: `${BASE_URL}/referenzen`,
  });

  return (
    <div className="min-h-screen bg-white" data-testid="referenzen-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
              <Users className="w-7 h-7 text-lab-accent" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Referenzen</h1>
              <p className="text-gray-400 mt-1">Was unsere Kunden über die Zusammenarbeit sagen</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        {testimonials.length === 0 ? (
          <p className="text-lab-text-muted text-center py-16">Noch keine Referenzen vorhanden.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="p-8 border border-lab-border bg-lab-bg-light"
                data-testid={`referenz-${t.id}`}
              >
                <Quote className="w-6 h-6 text-lab-accent mb-4 opacity-40" />
                <p className="text-lab-text text-base leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} className={`w-4 h-4 ${n <= t.rating ? "fill-lab-accent text-lab-accent" : "text-gray-200"}`} />
                  ))}
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-lab-border">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.client_name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-lab-accent/10 flex items-center justify-center text-lab-accent font-semibold">
                      {t.client_name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-lab-primary font-semibold text-sm">{t.client_name}</p>
                    <p className="text-lab-text-muted text-xs">{[t.position, t.company].filter(Boolean).join(", ")}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-lab-text-muted mb-4">Möchten auch Sie Ihre Prozesse transformieren?</p>
          <Link to="/termin" className="inline-block bg-lab-primary text-white px-8 py-3 text-sm font-semibold hover:bg-lab-primary/90 transition-colors" data-testid="referenzen-cta">
            Kostenloses Erstgespräch buchen
          </Link>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
